import React, { useCallback, useState, useEffect } from 'react';
import { string, bool } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import { isTooManyRequestsError } from '../../../util/errors';

import { Form, PrimaryButton, FieldTextInput, IconInquiry, Heading, FieldCurrencyInput, FieldRadioButton, IconClose, NamedLink, FieldSelect } from '../../../components';

import css from './InquiryOfferForm.module.css';

const ErrorMessage = props => {
  const { error } = props;
  // No transaction process attached to listing
  return error ? (
    <p className={css.error}>
      {error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="InquiryOfferForm.sendInquiryErrorNoProcess" />
      ) : isTooManyRequestsError(error) ? (
        <FormattedMessage id="InquiryOfferForm.tooManyRequestsError" />
      ) : (
        <FormattedMessage id="InquiryOfferForm.sendInquiryError" />
      )}
    </p>
  ) : null;
};

// NOTE: this InquiryOfferForm is only for booking & purchase processes
// The default-inquiry process is handled differently
const InquiryOfferFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        submitButtonWrapperClassName,
        formId,
        handleSubmit,
        inProgress,
        intl,
        listingTitle,
        authorDisplayName,
        sendInquiryError,
        isIframe,
        values,
        files,
        setFiles,
        listings,
        listingId,
        listingSlug
      } = fieldRenderProps;

      const messageLabel = intl.formatMessage(
        {
          id: 'InquiryOfferForm.messageLabel',
        },
        { authorDisplayName }
      );
      const messagePlaceholder = intl.formatMessage(
        {
          id: 'InquiryOfferForm.messagePlaceholder',
        },
        { authorDisplayName }
      );

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = submitInProgress;

      const onDrop = useCallback((acceptedFiles) => {
        const previewFiles = acceptedFiles.map(file => Object.assign(file, {
          preview: URL.createObjectURL(file)
        }));
        setFiles(prevState => [...prevState, ...previewFiles]);
      }, []);

      useEffect(() => {
        // Revoke the data uris to avoid memory leaks
        return () => files.forEach(file => URL.revokeObjectURL(file.preview));
      }, [files]);

      const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

      return (
        <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
          {!isIframe && <IconInquiry className={css.icon} />}
          <Heading as="h2" rootClassName={css.heading}>
            <FormattedMessage id="InquiryOfferForm.heading" values={{ listingTitle }} />
          </Heading>


          {listings?.length === 0 ? (
            <><p className={css.offerModalTitle}>
              You have no active products listed. List a product to make an offer.
            </p>

              <NamedLink name="NewListingPageWithCategory" className={css.offerModalButton} to={{ search: '?listing=' + listingId.uuid + '&slug=' + listingSlug }}>
                List a product to make an offer
              </NamedLink></>
          ) : null}

          {listings?.length > 0 ? (
            
            <>
            <FieldSelect
              className={css.field}
              name="listingId"
              id={formId ? `${formId}.listingId` : 'listingId'}
              label="Select a product to make an offer"
            >
              <option hidden value="">Select a product</option>
              {listings.map(listing => (
                <option key={listing.id.uuid} value={listing.id.uuid} disabled={listing?.attributes?.state !== 'published'}>
                  {listing.attributes.title} - {listing.attributes.state}
                </option>
              ))}
            </FieldSelect>

            <NamedLink name="NewListingPageWithCategory" className={css.offerModalButton} to={{ search: '?listing=' + listingId.uuid + '&slug=' + listingSlug }}>
              Or list a new product to make an offer
            </NamedLink></>
          ) : null}

          {/* <FieldTextInput
            className={css.field}
            type="textarea"
            name="message"
            id={formId ? `${formId}.message` : 'message'}
            label={messageLabel}
            placeholder={messagePlaceholder}
          /> */}
          <div className={submitButtonWrapperClassName}>
            <ErrorMessage error={sendInquiryError} />
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="InquiryOfferForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

InquiryOfferFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  isIframe: false,
  sendInquiryError: null,
};

InquiryOfferFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  inProgress: bool,
  isIframe: bool,

  listingTitle: string.isRequired,
  authorDisplayName: string.isRequired,
  sendInquiryError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const InquiryOfferForm = compose(injectIntl)(InquiryOfferFormComponent);

InquiryOfferForm.displayName = 'InquiryOfferForm';

export default InquiryOfferForm;
